import React, { useEffect, useMemo, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { toast } from "sonner";
import {
  Globe, FileText, MapPin, Tag, Plus, Save, Eye, RotateCcw, Archive,
  HelpCircle, Search,
  Trash2, ChevronUp, ChevronDown, Send, Layers, Filter, FilePlus,
  Image as ImageIcon, Upload, Route, Award, Home, CreditCard, Ship,
  Wrench, Info, Boxes, Sparkles,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import MediaLibrary from "./MediaLibrary";
import RoutesSubSection from "./RoutesSubSection";
import TrustedBrandsSubSection from "./TrustedBrandsSubSection";
import HomepageSubSection from "./HomepageSubSection";
import OnlinePaymentSubSection from "./OnlinePaymentSubSection";
import ServicePagesSubSection from "./ServicePagesSubSection";
import ToolPagesSubSection from "./ToolPagesSubSection";
import InfoPagesSubSection from "./InfoPagesSubSection";
import MiscPagesSubSection from "./MiscPagesSubSection";
import ExtraPagesSubSection from "./ExtraPagesSubSection";
import DestinationsSubSection from "./DestinationsSubSection";
import FAQCategoriesSubSection from "./FAQCategoriesSubSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  draft: "Concept",
  published: "Gepubliceerd",
  archived: "Offline",
  deleted: "Verwijderd",
};
const STATUS_COLORS = {
  draft: "bg-blue-100 text-blue-700 border-blue-200",
  published: "bg-green-100 text-green-700 border-green-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
  deleted: "bg-red-100 text-red-700 border-red-200",
};

const BLOCK_TYPES = [
  { value: "hero", label: "Hero block" },
  { value: "text_section", label: "Text section" },
  { value: "service_cards", label: "Service cards" },
  { value: "pricing", label: "Pricing block" },
  { value: "faq", label: "FAQ block" },
  { value: "location_card", label: "Location card" },
  { value: "cta_banner", label: "CTA banner" },
  { value: "benefits_list", label: "Benefits list (Phase 9)" },
  { value: "process_steps", label: "Process steps (Phase 9)" },
  { value: "agent_application_form", label: "Agent application form" },
  { value: "payment_reference_form", label: "Payment reference form" },
  { value: "stripe_buy_button", label: "Stripe Buy Button" },
  { value: "footer_block", label: "Footer block" },
  // Layout chrome singletons — only relevant on slug=footer / slug=header
  { value: "footer_columns", label: "Footer columns (layout)" },
  { value: "navigation_links", label: "Navigation links (layout)" },
  // Singletons used on tool / page bodies
  { value: "contact_block", label: "Contact block" },
  { value: "document_downloads", label: "Document downloads" },
  { value: "dangerous_goods_info", label: "Dangerous goods info" },
  { value: "pickup_delivery_calculator", label: "Pickup & delivery calculator" },
  { value: "product_highlights", label: "Product highlights" },
  { value: "location_cards", label: "Location cards" },
  { value: "form_intro", label: "Form intro" },
];

// Block types that are conceptually singletons per page. Mirrors the
// backend SINGLETON_BLOCK_TYPES list in admin_footer_dedupe.py — keeping
// the two lists in sync prevents the editor from offering a duplicate
// block that the backend would then reject with HTTP 409.
const SINGLETON_BLOCK_TYPES = new Set([
  "footer_columns",
  "navigation_links",
  "hero",
  "stripe_buy_button",
  "agent_application_form",
  "pickup_delivery_calculator",
  "document_downloads",
  "dangerous_goods_info",
  "contact_block",
]);

// Phase 18.15.8 + 18.16-A/B — block types that USED to render on a given
// public page but are now driven by a newer mechanism (homepage CMS,
// dedicated PageBanner, service-pages CMS, etc.). For the matching slug
// we badge these rows as "Vervangen" and disable inline editing so admins
// don't waste time tweaking content that is no longer rendered publicly.
//
// To onboard a new page: add an entry  slug -> Set([...block_type_keys])
const LEGACY_BLOCK_TYPES_PER_SLUG = {
  // /home — replaced by Websitebeheer → Homepage (Phase 18.15.7)
  home: new Set([
    "hero",
    "service_cards",
    "product_highlights",
    "cta_banner",
    "trust_strip",
  ]),
  // /online-betalen — replaced by the dedicated PageBanner injected via
  // App.js → CmsPageRenderer topSection (Phase 18.16-A).
  "online-betalen": new Set(["hero"]),
  // Phase 18.16-B Batch 1 — entire service pages now render via
  // ServicePageCmsWrapper using service_page_settings + cards. ALL
  // legacy CMS blocks (hero/text_section/service_cards/cta_banner/faq)
  // are now "Vervangen". Content blijft bewaard in CMS voor historie.
  zeevracht: new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  luchtvracht: new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  "shop-drop-ship": new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  moving: new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  verhuizing: new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  zakelijk: new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  "zakelijke-zendingen": new Set([
    "hero", "service_cards", "cta_banner", "text_section", "faq",
    "product_highlights", "trust_strip", "image_text",
  ]),
  // Phase 18.16-B Batch 2 — /word-agent banner moved to a dedicated
  // PageBanner module (CMS-driven) injected via topSection. The legacy
  // hero block on the CmsPage is now badged "Vervangen".
  "word-agent": new Set(["hero"]),
  // Phase 18.16-B Batch 3 — /documenten, /btw-terugvragen and /contact
  // banners moved to dedicated PageBanner modules (CMS-driven) injected
  // via topSection. The legacy hero block on each CmsPage is badged
  // "Vervangen". All other blocks (documents list, BTW steps, contact
  // form) continue to render normally.
  "documenten": new Set(["hero"]),
  "btw-terugvragen": new Set(["hero"]),
  "contact": new Set(["hero"]),
  // Phase 18.16-B Batch 4 — /my5999cargo banner moved to a dedicated
  // PageBanner module (CMS-driven) via My5999PageBanner. The legacy
  // hero block on the CmsPage is now badged "Vervangen". The
  // CmsExtraBlocks underneath still render normally.
  "my5999cargo": new Set(["hero"]),
  // Phase 18.17 Batch 5 — /hubs, /faq, /pickup-delivery banners moved
  // to dedicated ExtraPageBanner modules (CMS-driven) via topSection.
  // The legacy hero block on each CmsPage is badged "Vervangen". All
  // other CMS blocks continue to render normally.
  "hubs": new Set(["hero"]),
  "faq": new Set(["hero"]),
  "pickup-delivery": new Set(["hero"]),
};

// Per-slug copy + target tab for the legacy-banner shown on top of the
// Blocks tab. Slugs not listed here fall back to a generic message.
const LEGACY_BANNER_INFO_PER_SLUG = {
  home: {
    target_tab: "homepage",
    target_label: "Websitebeheer → Homepage",
    body: "Gebruik die module voor hero, service cards, sectie-koppen, KPI's en CTA-band. Sommige onderstaande blocks zijn vervangen en worden niet meer op de publieke homepage getoond.",
    button: "Open Homepage beheer",
  },
  "online-betalen": {
    target_tab: "online-payment",
    target_label: "Websitebeheer → Online betalen",
    body: "Beheer de premium banner (eyebrow, titel, highlight, subtitle en USP-badges) via de nieuwe Online betalen tab. De onderstaande hero block is bewaard voor historisch overzicht, maar wordt niet meer publiek gerenderd.",
    button: "Open Online betalen beheer",
  },
  zeevracht: {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Zeevracht",
    body: "De volledige /sea-freight pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  luchtvracht: {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Luchtvracht",
    body: "De volledige /air-freight pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  "shop-drop-ship": {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Shop, Drop & Ship",
    body: "De volledige /shop-drop-ship pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  moving: {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Verhuizing",
    body: "De volledige /moving pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  verhuizing: {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Verhuizing",
    body: "De volledige /verhuizing pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  zakelijk: {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Zakelijk",
    body: "De volledige /zakelijk pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  "zakelijke-zendingen": {
    target_tab: "service-pages",
    target_label: "Websitebeheer → Servicepagina's → Zakelijk",
    body: "Deze pagina wordt nu beheerd via de nieuwe Servicepagina's CMS. Onderstaande blocks zijn bewaard voor historisch overzicht maar worden niet meer publiek gerenderd.",
    button: "Open Servicepagina's beheer",
  },
  "word-agent": {
    target_tab: "tool-pages",
    target_label: "Websitebeheer → Tool-pagina's → Word agent",
    body: "De /word-agent pagina wordt nu beheerd via de nieuwe Tool-pagina's CMS (banner, USP-badges, intro en CTA-band). De onderstaande hero block is bewaard voor historisch overzicht maar wordt niet meer publiek gerenderd.",
    button: "Open Tool-pagina's beheer",
  },
  "documenten": {
    target_tab: "info-pages",
    target_label: "Websitebeheer → Informatiepagina's → Documenten",
    body: "De /documenten banner wordt nu beheerd via de nieuwe Informatiepagina's CMS (banner, USP-badges, intro en CTA-band). De documentenlijst en download-flow blijven volledig werken via de bestaande Documenten-tab; alleen de oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Informatiepagina's beheer",
  },
  "btw-terugvragen": {
    target_tab: "info-pages",
    target_label: "Websitebeheer → Informatiepagina's → BTW-teruggave",
    body: "De /btw-terugvragen banner wordt nu beheerd via de nieuwe Informatiepagina's CMS (banner, USP-badges, intro en CTA-band). De inhoud-blocks (stappenplan, waarschuwingen) onder de banner blijven gewoon werken. Alleen de oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Informatiepagina's beheer",
  },
  "contact": {
    target_tab: "info-pages",
    target_label: "Websitebeheer → Informatiepagina's → Contact",
    body: "De /contact banner wordt nu beheerd via de nieuwe Informatiepagina's CMS (banner, USP-badges, intro en CTA-band). Het contactformulier, offerteformulier (#quote-form anchor), hubs-grid en bedrijfsgegevens blijven volledig werken via de bestaande modules. Alleen de oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Informatiepagina's beheer",
  },
  "my5999cargo": {
    target_tab: "misc-pages",
    target_label: "Websitebeheer → Overige pagina's → MY5999",
    body: "De /my5999cargo banner wordt nu beheerd via de nieuwe Overige pagina's CMS (banner, USP-badges en login-CTA). De ManagerCargo login-knop, drie feature-cards en CmsExtraBlocks onderaan blijven werken. Alleen de oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Overige pagina's beheer",
  },
  "hubs": {
    target_tab: "extra-pages",
    target_label: "Websitebeheer → Extra pagina's → Hubs",
    body: "De /hubs banner wordt nu beheerd via de nieuwe Extra pagina's CMS (banner, USP-badges, CTA-band). De hubs-lijst zelf met vlaggen wordt live geladen uit Websitebeheer → Locaties (vergeet daar de country_code in te vullen). De oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Extra pagina's beheer",
  },
  "faq": {
    target_tab: "extra-pages",
    target_label: "Websitebeheer → Extra pagina's → FAQ",
    body: "De /faq banner wordt nu beheerd via de nieuwe Extra pagina's CMS (banner, USP-badges, CTA-band). De FAQ-items blijven via de bestaande CMS-blocks beheerbaar. De oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Extra pagina's beheer",
  },
  "pickup-delivery": {
    target_tab: "extra-pages",
    target_label: "Websitebeheer → Extra pagina's → Pickup & Delivery",
    body: "De /pickup-delivery banner wordt nu beheerd via de nieuwe Extra pagina's CMS (banner, USP-badges, CTA-band). De pickup-calculator en eventuele content-blocks blijven werken. De oude hero block is bewaard voor historisch overzicht en wordt niet meer publiek gerenderd.",
    button: "Open Extra pagina's beheer",
  },
};

// Backwards-compatible alias — older code paths still reference this.
const HOMEPAGE_LEGACY_BLOCK_TYPES = LEGACY_BLOCK_TYPES_PER_SLUG.home;

function isLegacyBlockForSlug(slug, blockType) {
  const set = LEGACY_BLOCK_TYPES_PER_SLUG[slug];
  return !!(set && set.has(blockType));
}

const BLOCK_FIELDS = {
  hero: ["title", "subtitle", "text", "image_url", "image_alt", "image_title", "image_position", "overlay_style", "button_1_text", "button_1_link", "button_2_text", "button_2_link"],
  text_section: ["title", "text", "image_url", "button_text", "button_link"],
  service_cards: ["title", "subtitle", "cards_json"],
  pricing: ["title", "description", "line", "price", "currency", "vat_included", "note"],
  faq: ["question", "answer", "sort_order"],
  location_card: ["country", "country_code", "hub_label", "address", "phone", "email", "contact_person", "opening_hours", "button_link"],
  cta_banner: ["title", "text", "button_text", "button_link"],
  footer_block: ["column_title", "links_json", "contact_text", "legal_text"],
  // Layout chrome singleton — Release 1.C footer fix.
  // `tagline` and `legal_text` are first-class scalar fields so admins
  // can edit the copyright/branding lines without having to touch the
  // raw JSON `columns` structure. (The `columns` array remains editable
  // via the History/raw-content tab; we deliberately keep it out of the
  // simple editor so a single typo can't break the entire footer.)
  footer_columns: ["tagline", "legal_text"],
};

// Field-level hints for the admin BlockEditor. Only shown when present.
const FIELD_HINTS = {
  image_url: "Volledige URL naar de banner. Leeg laten = mooie gradient fallback.",
  image_alt: "Alt-tekst voor toegankelijkheid en SEO.",
  image_title: "Optionele title-tooltip bij hover.",
  image_position: "center · top · bottom · left · right (object-position).",
  overlay_style: "blue_gradient (default) · dark_gradient · none.",
  legal_text: "Bv: '© 2026 5999Cargo Group Europe. Alle rechten voorbehouden.' Leeg laten = geen copyrightregel.",
  tagline: "Korte zin onder het logo in de footer.",
};

// ============================================================
// Sub-section: Pages
// ============================================================
function PagesSubSection({ onOpenHomepageTab }) {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const canCreate = hasPermission("website:create");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter === "deleted") {
        params.set("include_deleted", "true");
        params.set("status", "deleted");
      } else if (statusFilter !== "all") {
        params.set("status", statusFilter);
      }
      const qs = params.toString();
      const r = await adminApi.get(`/admin/website/pages${qs ? `?${qs}` : ""}`);
      setPages(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (!newSlug.trim()) { toast.error("Slug is verplicht."); return; }
    try {
      const r = await adminApi.post("/admin/website/pages", {
        slug: newSlug.trim(),
        nav_label: newLabel.trim() || newSlug.trim(),
      });
      toast.success("Pagina aangemaakt");
      setCreating(false); setNewSlug(""); setNewLabel("");
      await load();
      setActiveId(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (activeId) {
    return <PageDetail pageId={activeId} onBack={() => { setActiveId(null); load(); }} onOpenHomepageTab={onOpenHomepageTab} />;
  }

  return (
    <div data-testid="website-pages-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-gray-600">Beheer publieke pagina's, SEO, blokken en versiehistorie.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center bg-white border border-gray-200 px-2 py-1 gap-2" data-testid="website-pages-filter">
            <Filter className="w-3.5 h-3.5 text-gray-500" />
            {[
              { v: "all",        label: "Alle" },
              { v: "draft",      label: "Concept" },
              { v: "published",  label: "Gepubliceerd" },
              { v: "archived",   label: "Offline" },
              { v: "deleted",    label: "Verwijderd" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setStatusFilter(opt.v)}
                data-testid={`website-pages-filter-${opt.v}`}
                className={`text-[11px] uppercase tracking-wide font-chivo font-bold px-2 py-1 transition-colors ${statusFilter === opt.v ? "bg-[#0A192F] text-white" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {canCreate && (
            <Button onClick={() => setCreating(!creating)} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="website-page-new-button">
              <Plus className="w-4 h-4 mr-1" /> Nieuwe pagina
            </Button>
          )}
        </div>
      </div>

      {creating && canCreate && (
        <div className="bg-white border border-gray-200 p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Slug</Label>
            <Input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} className="rounded-none mt-1" placeholder="bv. nieuwe-dienst" data-testid="website-page-new-slug" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Nav-label</Label>
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="rounded-none mt-1" placeholder="Menu-naam" />
          </div>
          <Button onClick={onCreate} className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs h-10" data-testid="website-page-new-save">
            Aanmaken
          </Button>
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500">Laden…</div>
        ) : pages.length === 0 ? (
          <div className="p-6 text-gray-500">Nog geen pagina's.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="py-3 px-4 font-bold">Slug</th>
                <th className="py-3 px-4 font-bold">Label</th>
                <th className="py-3 px-4 font-bold">Status</th>
                <th className="py-3 px-4 font-bold">Versie</th>
                <th className="py-3 px-4 font-bold">Bijgewerkt</th>
                <th className="py-3 px-4 font-bold">Concept-wijzigingen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pages.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => setActiveId(p.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                  data-testid={`website-page-row-${p.slug}`}
                >
                  <td className="py-3 px-4 font-mono text-xs">/{p.slug}</td>
                  <td className="py-3 px-4">{p.nav_label}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold border ${STATUS_COLORS[p.status]}`}>
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs">{p.published_version || 0}</td>
                  <td className="py-3 px-4 text-xs text-gray-500">{p.updated_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-3 px-4 text-xs">
                    {p.draft_dirty ? <span className="text-orange-600 font-bold">Concept</span> : <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Page detail
// ============================================================
function PageDetail({ pageId, onBack, onOpenHomepageTab }) {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");
  const [versions, setVersions] = useState([]);
  const [previewData, setPreviewData] = useState(null);

  const canEdit = hasPermission("website:edit");
  const canPublish = hasPermission("website:publish");
  const canArchive = hasPermission("website:archive");
  const canRestore = hasPermission("website:restore");
  const canDelete = hasPermission("website:delete");
  const canPermanentDelete = hasPermission("website:permanent_delete");
  const canUnpublishToDraft = canEdit && canPublish;
  const canDeleteBlock = hasPermission("website:delete_block");

  // Permanent delete dialog state
  const [permDeleteOpen, setPermDeleteOpen] = useState(false);
  const [permDeleteConfirmText, setPermDeleteConfirmText] = useState("");
  const [permDeleting, setPermDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        adminApi.get(`/admin/website/pages/${pageId}`),
        adminApi.get(`/admin/website/pages/${pageId}/versions`),
      ]);
      setPage(r.data);
      setVersions(v.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [pageId]);

  useEffect(() => { load(); }, [load]);

  const updateField = (path, value) => {
    setPage((p) => {
      const next = JSON.parse(JSON.stringify(p));
      const parts = path.split(".");
      let target = next;
      for (let i = 0; i < parts.length - 1; i++) {
        target[parts[i]] = target[parts[i]] || {};
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const onSaveDraft = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await adminApi.put(`/admin/website/pages/${pageId}`, {
        nav_label: page.nav_label,
        seo: page.seo,
        draft_content: page.draft_content,
      });
      toast.success("Concept opgeslagen");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onPublish = async () => {
    if (!canPublish) return;
    if (!window.confirm("Concept publiceren? Live website wordt direct bijgewerkt.")) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/publish`);
      toast.success("Gepubliceerd");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onArchive = async () => {
    if (!canArchive) return;
    if (!window.confirm("Pagina offline halen? Bezoekers krijgen 404. Content blijft bewaard.")) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/archive`);
      toast.success("Pagina staat nu offline");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onSoftDelete = async () => {
    if (!canDelete) return;
    if (!window.confirm("Weet u zeker dat u deze pagina wilt verwijderen?\n\nDe pagina wordt soft-deleted: bezoekers krijgen 404 en de pagina verdwijnt uit de standaard lijst. U kunt hem later herstellen via het 'Verwijderd' filter.")) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/delete`);
      toast.success("Pagina verwijderd (soft delete)");
      onBack();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onRestorePage = async () => {
    if (!canRestore) return;
    if (!window.confirm("Pagina herstellen?\n\nDe pagina komt terug als concept. Publiceer hem expliciet om hem weer publiek zichtbaar te maken.")) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/restore-page`);
      toast.success("Pagina hersteld als concept");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDraftFromPublished = async () => {
    if (!canEdit) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/draft-from-published`);
      toast.success("Nieuwe conceptversie gemaakt op basis van de gepubliceerde versie");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUnpublishToDraft = async () => {
    if (!canUnpublishToDraft) return;
    if (!window.confirm(
      "Pagina terugzetten naar concept?\n\nDeze pagina wordt offline gehaald en als concept opgeslagen. " +
      "Bezoekers kunnen deze pagina niet meer publiek bekijken totdat u opnieuw publiceert.",
    )) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/unpublish-to-draft`);
      toast.success("Pagina is terug naar concept gezet");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitPermanentDelete = async () => {
    if (!canPermanentDelete) return;
    if (permDeleteConfirmText !== "PERMANENT VERWIJDEREN") {
      toast.error("Typ exact 'PERMANENT VERWIJDEREN' om door te gaan");
      return;
    }
    setPermDeleting(true);
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/permanent-delete`);
      toast.success("Pagina is permanent verwijderd");
      setPermDeleteOpen(false);
      setPermDeleteConfirmText("");
      onBack();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPermDeleting(false); }
  };

  const onRestore = async (versionId) => {
    if (!canRestore) return;
    if (!window.confirm("Versie herstellen als nieuw concept? De huidige live versie blijft staan tot je opnieuw publiceert.")) return;
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/versions/${versionId}/restore`);
      toast.success("Versie als concept hersteld");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onPreview = async () => {
    try {
      const r = await adminApi.get(`/admin/website/pages/${pageId}/preview`);
      setPreviewData(r.data);
      setTab("preview");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onAddBlock = async (type) => {
    try {
      await adminApi.post(`/admin/website/pages/${pageId}/blocks`, {
        type, sort_order: (page.draft_content?.blocks?.length || 0), active: true, data: {},
      });
      toast.success("Block toegevoegd");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpdateBlock = async (block) => {
    try {
      await adminApi.put(`/admin/website/pages/${pageId}/blocks/${block.id}`, {
        type: block.type, sort_order: block.sort_order, active: block.active, data: block.data,
      });
      toast.success("Block opgeslagen");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDeleteBlock = async (blockId) => {
    if (!canDeleteBlock) { toast.error("Geen rechten om blocks te verwijderen."); return; }
    if (!window.confirm("Block verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/website/pages/${pageId}/blocks/${blockId}`);
      toast.success("Block verwijderd");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onMoveBlock = async (block, dir) => {
    const blocks = (page.draft_content?.blocks || []).slice();
    const i = blocks.findIndex((b) => b.id === block.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= blocks.length) return;
    const a = blocks[i], b = blocks[j];
    a.sort_order = j; b.sort_order = i;
    await onUpdateBlock(a);
    await onUpdateBlock(b);
  };

  if (loading || !page) {
    return <div className="py-16 text-center text-gray-500">Laden…</div>;
  }

  const seo = page.seo || {};
  const draft = page.draft_content || { blocks: [] };
  const blocks = (draft.blocks || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div data-testid={`website-page-detail-${page.slug}`} className="space-y-4">
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button onClick={onBack} className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-bold mb-2 hover:underline" data-testid="website-page-back">
              ← Terug naar paginalijst
            </button>
            <h1 className="font-chivo font-black text-2xl text-[#0A192F] flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#0055FF]" /> {page.nav_label}
            </h1>
            <div className="text-xs text-gray-500 mt-1 font-mono">/{page.slug}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wide font-bold border ${STATUS_COLORS[page.status]}`} data-testid="website-page-status-badge">
              {STATUS_LABELS[page.status] || page.status} v{page.published_version || 0}
            </span>
            {page.draft_dirty && (
              <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-wide font-bold border bg-orange-100 text-orange-700 border-orange-200" data-testid="website-page-draft-dirty-badge">
                Concept-wijzigingen
              </span>
            )}
            <Button onClick={onPreview} variant="outline" size="sm" className="rounded-none" data-testid="website-page-preview">
              <Eye className="w-4 h-4 mr-1" /> Preview
            </Button>

            {/* Action buttons by status */}
            {page.status === "deleted" ? (
              <>
                {canRestore && (
                  <Button onClick={onRestorePage} size="sm" className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid="website-page-restore-page">
                    <RotateCcw className="w-4 h-4 mr-1" /> Herstellen
                  </Button>
                )}
                {canPermanentDelete && (
                  <Button onClick={() => setPermDeleteOpen(true)} variant="outline" size="sm" className="rounded-none text-red-700 border-red-300 hover:bg-red-50" data-testid="website-page-permanent-delete">
                    <Trash2 className="w-4 h-4 mr-1" /> Permanent verwijderen
                  </Button>
                )}
              </>
            ) : (
              <>
                {canEdit && (
                  <Button onClick={onSaveDraft} disabled={saving} variant="outline" size="sm" className="rounded-none" data-testid="website-page-save-draft">
                    <Save className="w-4 h-4 mr-1" /> Opslaan als concept
                  </Button>
                )}
                {/* For published: allow creating a new draft from published */}
                {canEdit && page.status === "published" && !page.draft_dirty && (
                  <Button onClick={onDraftFromPublished} variant="outline" size="sm" className="rounded-none" data-testid="website-page-draft-from-published">
                    <FilePlus className="w-4 h-4 mr-1" /> Nieuwe conceptversie
                  </Button>
                )}
                {/* For published: hard unpublish back to draft */}
                {canUnpublishToDraft && page.status === "published" && (
                  <Button onClick={onUnpublishToDraft} variant="outline" size="sm" className="rounded-none" data-testid="website-page-unpublish-to-draft">
                    <RotateCcw className="w-4 h-4 mr-1" /> Terug naar concept
                  </Button>
                )}
                {canPublish && (
                  <Button onClick={onPublish} size="sm" className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid="website-page-publish">
                    <Send className="w-4 h-4 mr-1" /> {page.status === "archived" ? "Opnieuw publiceren" : "Publiceren"}
                  </Button>
                )}
                {canArchive && page.status === "published" && (
                  <Button onClick={onArchive} variant="outline" size="sm" className="rounded-none text-gray-600" data-testid="website-page-archive">
                    <Archive className="w-4 h-4 mr-1" /> Offline halen
                  </Button>
                )}
                {canRestore && page.status === "archived" && (
                  <Button onClick={onRestorePage} variant="outline" size="sm" className="rounded-none" data-testid="website-page-restore-page">
                    <RotateCcw className="w-4 h-4 mr-1" /> Naar concept
                  </Button>
                )}
                {canDelete && (
                  <Button onClick={onSoftDelete} variant="outline" size="sm" className="rounded-none text-red-600 border-red-200 hover:bg-red-50" data-testid="website-page-delete">
                    <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Permanent delete confirm dialog */}
          <Dialog open={permDeleteOpen} onOpenChange={(o) => { if (!o) { setPermDeleteOpen(false); setPermDeleteConfirmText(""); } }}>
            <DialogContent data-testid="website-page-permanent-delete-dialog">
              <DialogHeader>
                <DialogTitle className="text-red-700">Pagina permanent verwijderen?</DialogTitle>
                <DialogDescription>
                  Deze actie kan niet ongedaan worden gemaakt. De pagina, conceptinhoud en versiegeschiedenis worden definitief verwijderd. Audit logs blijven bewaard.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 text-sm px-3 py-2">
                  <div className="font-chivo font-bold uppercase text-xs">Pagina</div>
                  <div className="font-mono text-xs">{page.slug}</div>
                </div>
                <Label className="text-xs">Typ <span className="font-mono font-bold text-red-700">PERMANENT VERWIJDEREN</span> om te bevestigen</Label>
                <Input
                  data-testid="website-page-permanent-delete-confirm-input"
                  value={permDeleteConfirmText}
                  onChange={(e) => setPermDeleteConfirmText(e.target.value)}
                  className="rounded-none font-mono"
                  placeholder="PERMANENT VERWIJDEREN"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => { setPermDeleteOpen(false); setPermDeleteConfirmText(""); }}>
                  Annuleren
                </Button>
                <Button
                  onClick={submitPermanentDelete}
                  disabled={permDeleting || permDeleteConfirmText !== "PERMANENT VERWIJDEREN"}
                  className="rounded-none bg-red-700 hover:bg-red-800 text-white font-chivo font-bold uppercase text-xs"
                  data-testid="website-page-permanent-delete-submit"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Definitief verwijderen
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-white border border-gray-200 p-0 h-auto flex flex-wrap" data-testid="website-page-tabs">
          <TabsTrigger value="seo" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="website-page-tab-seo">SEO</TabsTrigger>
          <TabsTrigger value="content" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="website-page-tab-content">Content</TabsTrigger>
          <TabsTrigger value="blocks" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="website-page-tab-blocks">Blocks ({blocks.length})</TabsTrigger>
          <TabsTrigger value="versions" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="website-page-tab-versions">Versies ({versions.length})</TabsTrigger>
          <TabsTrigger value="preview" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="website-page-tab-preview">Preview</TabsTrigger>
        </TabsList>

        {/* SEO */}
        <TabsContent value="seo" className="bg-white border border-gray-200 p-6 mt-2 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">SEO Title</Label>
            <Input value={seo.title || ""} onChange={(e) => updateField("seo.title", e.target.value)} className="rounded-none mt-1" data-testid="website-page-seo-title" disabled={!canEdit} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Meta Description</Label>
            <Textarea rows={3} value={seo.meta_description || ""} onChange={(e) => updateField("seo.meta_description", e.target.value)} className="rounded-none mt-1" data-testid="website-page-seo-meta" disabled={!canEdit} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!seo.no_index} onCheckedChange={(v) => updateField("seo.no_index", v)} disabled={!canEdit} data-testid="website-page-seo-noindex" />
            <Label className="text-xs uppercase tracking-wide font-bold">No-index (uitsluiten van Google)</Label>
          </div>
        </TabsContent>

        {/* Content */}
        <TabsContent value="content" className="bg-white border border-gray-200 p-6 mt-2 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Page Title (publieke titel)</Label>
            <Input value={draft.page_title || ""} onChange={(e) => updateField("draft_content.page_title", e.target.value)} className="rounded-none mt-1" data-testid="website-page-content-title" disabled={!canEdit} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">H1</Label>
            <Input value={draft.h1 || ""} onChange={(e) => updateField("draft_content.h1", e.target.value)} className="rounded-none mt-1" data-testid="website-page-content-h1" disabled={!canEdit} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Intro / paragraph</Label>
            <Textarea rows={4} value={draft.intro || ""} onChange={(e) => updateField("draft_content.intro", e.target.value)} className="rounded-none mt-1" data-testid="website-page-content-intro" disabled={!canEdit} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Nav-label</Label>
            <Input value={page.nav_label || ""} onChange={(e) => updateField("nav_label", e.target.value)} className="rounded-none mt-1" disabled={!canEdit} />
          </div>
        </TabsContent>

        {/* Blocks */}
        <TabsContent value="blocks" className="bg-white border border-gray-200 p-6 mt-2">
          {/* Phase 18.15.8 + 18.16-A — legacy banner: when the public page
              is now driven by a newer mechanism (Homepage CMS, dedicated
              PageBanner, etc.), make that clear so admins don't waste
              time editing blocks that no longer render. */}
          {LEGACY_BANNER_INFO_PER_SLUG[page.slug] && (
            (() => {
              const info = LEGACY_BANNER_INFO_PER_SLUG[page.slug];
              return (
                <div
                  className="mb-5 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3"
                  data-testid={`page-blocks-legacy-banner-${page.slug}`}
                >
                  <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center text-sm font-bold">!</div>
                  <div className="flex-1 text-sm text-amber-900">
                    <strong>Deze pagina wordt beheerd via {info.target_label}.</strong>
                    <br />
                    {info.body}
                  </div>
                  {info.button && info.target_tab && (
                    <Button
                      onClick={() => onOpenHomepageTab?.(info.target_tab)}
                      size="sm"
                      className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                      data-testid={`page-blocks-open-target-${page.slug}`}
                    >
                      {info.button}
                    </Button>
                  )}
                </div>
              );
            })()
          )}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-chivo font-bold text-sm uppercase tracking-wide">Content blocks</h3>
            <div className="flex items-center gap-2">
              <Select onValueChange={onAddBlock} disabled={!canEdit}>
                <SelectTrigger className="rounded-none w-56" data-testid="website-block-add-select"><SelectValue placeholder="+ Voeg block toe…" /></SelectTrigger>
                <SelectContent>
                  {BLOCK_TYPES.map((bt) => {
                    // Mirror the backend singleton guard — check BOTH draft and
                    // published blocks. Pages whose draft has diverged from
                    // published (historical /home for example) could otherwise
                    // accidentally inject a second singleton.
                    const draftBlocks = (page.draft_content?.blocks) || [];
                    const publishedBlocks = (page.published_content?.blocks) || [];
                    const alreadyExists =
                      SINGLETON_BLOCK_TYPES.has(bt.value) &&
                      (draftBlocks.some((b) => b.type === bt.value && b.active !== false) ||
                        publishedBlocks.some((b) => b.type === bt.value && b.active !== false));
                    const isLegacyForPage = isLegacyBlockForSlug(page.slug, bt.value);
                    const info = LEGACY_BANNER_INFO_PER_SLUG[page.slug];
                    const legacySuffix = isLegacyForPage && info
                      ? ` — vervangen door ${info.target_label}`
                      : isLegacyForPage
                        ? " — vervangen"
                        : "";
                    return (
                      <SelectItem
                        key={bt.value}
                        value={bt.value}
                        disabled={alreadyExists || isLegacyForPage}
                        data-testid={`website-block-add-option-${bt.value}`}
                      >
                        {bt.label}
                        {alreadyExists ? " — al aanwezig" : ""}
                        {legacySuffix}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          {blocks.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen blocks. Voeg er één toe om de pagina op te bouwen.</p>
          ) : (
            <ul className="space-y-3">
              {blocks.map((b, idx) => (
                <BlockEditor
                  key={b.id}
                  block={b}
                  canEdit={canEdit}
                  canDelete={canDeleteBlock}
                  isFirst={idx === 0}
                  isLast={idx === blocks.length - 1}
                  isLegacyForHome={isLegacyBlockForSlug(page.slug, b.type)}
                  legacyTargetLabel={LEGACY_BANNER_INFO_PER_SLUG[page.slug]?.target_label}
                  onChange={(updated) => onUpdateBlock(updated)}
                  onDelete={() => onDeleteBlock(b.id)}
                  onMoveUp={() => onMoveBlock(b, -1)}
                  onMoveDown={() => onMoveBlock(b, +1)}
                />
              ))}
            </ul>
          )}
        </TabsContent>

        {/* Versions */}
        <TabsContent value="versions" className="bg-white border border-gray-200 p-6 mt-2">
          {versions.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen versies vastgelegd.</p>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="website-page-versions-list">
              {versions.map((v) => (
                <li key={v.id} className="py-3 flex items-center justify-between gap-3" data-testid={`website-page-version-${v.version}`}>
                  <div>
                    <div className="font-chivo font-bold text-sm">v{v.version} — {STATUS_LABELS[v.status] || v.status}</div>
                    <div className="text-xs text-gray-500">{v.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                  {canRestore && (
                    <Button onClick={() => onRestore(v.id)} variant="outline" size="sm" className="rounded-none" data-testid={`website-page-restore-${v.version}`}>
                      <RotateCcw className="w-4 h-4 mr-1" /> Herstellen als concept
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        {/* Preview */}
        <TabsContent value="preview" className="bg-white border border-gray-200 p-6 mt-2">
          {!previewData ? (
            <p className="text-sm text-gray-500">Klik op "Preview" om het concept te bekijken.</p>
          ) : (
            <div data-testid="website-page-preview-pane">
              <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-bold mb-2">PREVIEW — niet geïndexeerd, niet publiek zichtbaar</div>
              <h1 className="font-chivo font-black text-3xl text-[#0A192F]">{previewData.content?.h1 || previewData.content?.page_title}</h1>
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{previewData.content?.intro}</p>
              <div className="mt-4 space-y-2">
                {(previewData.content?.blocks || []).map((b) => (
                  <div key={b.id} className="border border-gray-200 p-3 bg-gray-50">
                    <div className="text-xs uppercase tracking-wide font-bold text-[#0055FF]">{b.type}</div>
                    <pre className="text-xs mt-1 overflow-x-auto">{JSON.stringify(b.data, null, 2)}</pre>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Block editor (generic key/value)
// ============================================================
function BlockEditor({ block, canEdit, canDelete, isFirst, isLast, isLegacyForHome, legacyTargetLabel, onChange, onDelete, onMoveUp, onMoveDown }) {
  const [local, setLocal] = useState(block);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  useEffect(() => setLocal(block), [block.id, block.sort_order, block.active]);

  const fields = BLOCK_FIELDS[block.type] || Object.keys(block.data || {});
  // Phase 18.15.8 — when a block type has been superseded by the new
  // Websitebeheer → Homepage module, dim the row and disable inline
  // editing so admins don't waste time tweaking content that is no
  // longer rendered by the public HomePage.jsx.
  const legacyDisabled = !!isLegacyForHome;
  const effectiveCanEdit = canEdit && !legacyDisabled;
  const targetLabel = legacyTargetLabel || "Websitebeheer → Homepage";

  return (
    <li
      className={`border border-gray-200 p-4 ${legacyDisabled ? "bg-amber-50/40 border-amber-200" : "bg-white"}`}
      data-testid={`website-block-${block.id}`}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">{block.type}</span>
          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${block.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
            {block.active ? "Actief" : "Inactief"}
          </span>
          {legacyDisabled && (
            <span
              className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold bg-amber-200 text-amber-900"
              data-testid={`website-block-legacy-badge-${block.id}`}
              title="Wordt niet meer op deze pagina gerenderd"
            >
              Vervangen
            </span>
          )}
          {legacyDisabled && (
            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold bg-slate-200 text-slate-800">
              Niet meer zichtbaar op deze pagina
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} disabled={isFirst || legacyDisabled} className="p-1 text-gray-500 hover:text-[#0055FF] disabled:opacity-30" title="Omhoog" data-testid={`website-block-up-${block.id}`}>
            <ChevronUp className="w-4 h-4" />
          </button>
          <button onClick={onMoveDown} disabled={isLast || legacyDisabled} className="p-1 text-gray-500 hover:text-[#0055FF] disabled:opacity-30" title="Omlaag" data-testid={`website-block-down-${block.id}`}>
            <ChevronDown className="w-4 h-4" />
          </button>
          {canEdit && !legacyDisabled && (
            <button
              onClick={() => onChange({ ...local, active: !local.active })}
              className="px-2 text-xs text-gray-600 hover:text-[#0055FF]"
              data-testid={`website-block-toggle-${block.id}`}
            >
              {local.active ? "Deactiveren" : "Activeren"}
            </button>
          )}
          {canDelete && !legacyDisabled && (
            <button onClick={onDelete} className="p-1 text-gray-400 hover:text-red-500" title="Verwijderen" data-testid={`website-block-delete-${block.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {legacyDisabled && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-100/70 border border-amber-200 text-xs text-amber-900" data-testid={`website-block-legacy-note-${block.id}`}>
          Dit block is vervangen door <strong>{targetLabel}</strong>. Wijzigen heeft geen effect op de publieke pagina. De inhoud blijft bewaard voor historisch overzicht.
        </div>
      )}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${legacyDisabled ? "pointer-events-none opacity-60 select-none" : ""}`}
        aria-disabled={legacyDisabled || undefined}
      >
        {fields.map((f) => {
          const isLong = f.includes("text") || f.includes("description") || f.includes("answer") || f.endsWith("_json");
          const isImageUrl = f === "image_url";
          const imageUrlValue = (local.data?.image_url || "").trim();
          const isImagePosition = f === "image_position";
          const isOverlayStyle = f === "overlay_style";
          return (
            <div key={f} className={isLong || isImageUrl ? "sm:col-span-2" : ""}>
              <Label className="text-xs uppercase tracking-wide font-bold">{f.replace(/_/g, " ")}</Label>
              {isImagePosition ? (
                <select
                  value={local.data?.[f] || "center"}
                  onChange={(e) => setLocal({ ...local, data: { ...local.data, [f]: e.target.value } })}
                  disabled={!canEdit}
                  className="w-full mt-1 rounded-xl border border-gray-300 bg-white px-3 h-10 text-sm"
                  data-testid={`website-block-field-${f}-${block.id}`}
                >
                  <option value="center">center</option>
                  <option value="top">top</option>
                  <option value="bottom">bottom</option>
                  <option value="left">left</option>
                  <option value="right">right</option>
                </select>
              ) : isOverlayStyle ? (
                <select
                  value={local.data?.[f] || "blue_gradient"}
                  onChange={(e) => setLocal({ ...local, data: { ...local.data, [f]: e.target.value } })}
                  disabled={!canEdit}
                  className="w-full mt-1 rounded-xl border border-gray-300 bg-white px-3 h-10 text-sm"
                  data-testid={`website-block-field-${f}-${block.id}`}
                >
                  <option value="blue_gradient">blue_gradient</option>
                  <option value="dark_gradient">dark_gradient</option>
                  <option value="none">none</option>
                </select>
              ) : isImageUrl ? (
                <>
                  <Input
                    value={local.data?.[f] ?? ""}
                    onChange={(e) => setLocal({ ...local, data: { ...local.data, [f]: e.target.value } })}
                    className="rounded-none mt-1"
                    disabled={!canEdit}
                    placeholder="https://… of /api/media/{id}"
                    data-testid={`website-block-field-${f}-${block.id}`}
                  />
                  {canEdit && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <input
                        id={`hero-upload-${block.id}`}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                        hidden
                        data-testid={`hero-upload-input-${block.id}`}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error("Bestand is groter dan 5 MB");
                            e.target.value = "";
                            return;
                          }
                          const fd = new FormData();
                          fd.append("file", file);
                          fd.append("alt_text", local.data?.image_alt || "");
                          try {
                            const r = await adminApi.post("/admin/media/upload", fd, { headers: { "Content-Type": undefined } });
                            const next = {
                              ...local,
                              data: {
                                ...local.data,
                                image_url: r.data.url,
                                image_alt: local.data?.image_alt || r.data.alt_text || "",
                              },
                            };
                            setLocal(next);
                            toast.success("Geüpload en gekoppeld");
                          } catch (err) {
                            toast.error(formatApiError(err, "Upload mislukt"));
                          } finally {
                            e.target.value = "";
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => document.getElementById(`hero-upload-${block.id}`)?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-[11px] font-chivo font-bold uppercase tracking-wide"
                        data-testid={`hero-upload-btn-${block.id}`}
                      >
                        <Upload className="w-3.5 h-3.5" />Upload nieuw
                      </button>
                      <button
                        type="button"
                        onClick={() => setMediaPickerOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-gray-300 text-[#082c50] hover:border-brand-500 hover:text-brand-500 text-[11px] font-chivo font-bold uppercase tracking-wide"
                        data-testid={`hero-pick-btn-${block.id}`}
                      >
                        <ImageIcon className="w-3.5 h-3.5" />Kies uit Media
                      </button>
                      {imageUrlValue && (
                        <button
                          type="button"
                          onClick={() => setLocal({ ...local, data: { ...local.data, image_url: "" } })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 text-[11px] font-chivo font-bold uppercase tracking-wide"
                          data-testid={`website-block-clear-image-${block.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />Verwijderen
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : isLong ? (
                <Textarea
                  rows={3}
                  value={local.data?.[f] ?? ""}
                  onChange={(e) => setLocal({ ...local, data: { ...local.data, [f]: e.target.value } })}
                  className="rounded-none mt-1 font-mono text-xs"
                  disabled={!canEdit}
                  data-testid={`website-block-field-${f}-${block.id}`}
                />
              ) : (
                <Input
                  value={local.data?.[f] ?? ""}
                  onChange={(e) => setLocal({ ...local, data: { ...local.data, [f]: e.target.value } })}
                  className="rounded-none mt-1"
                  disabled={!canEdit}
                  data-testid={`website-block-field-${f}-${block.id}`}
                />
              )}
              {FIELD_HINTS[f] && (
                <p className="mt-1 text-[10px] text-gray-500 leading-snug">{FIELD_HINTS[f]}</p>
              )}
              {isImageUrl && imageUrlValue && (
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={imageUrlValue}
                    alt="Hero preview"
                    className="w-32 h-16 object-cover border border-gray-200 rounded-xl"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid={`website-block-image-preview-${block.id}`}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setLocal({ ...local, data: { ...local.data, image_url: "" } })}
                      className="text-xs text-red-600 hover:text-red-700 font-chivo font-bold uppercase tracking-wide"
                      data-testid={`website-block-clear-image-${block.id}`}
                    >
                      Verwijder afbeelding
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {canEdit && (
        <div className="mt-3 flex justify-end">
          <Button onClick={() => onChange(local)} size="sm" className="rounded-none bg-[#050505] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid={`website-block-save-${block.id}`}>
            <Save className="w-4 h-4 mr-1" /> Block opslaan
          </Button>
        </div>
      )}

      {/* Phase 18.15.3 — Media Library picker dialog (Hero block etc.).
          State `mediaPickerOpen` was reachable but no dialog rendered
          it, so admins could open the picker only on pages that owned
          their own modal. We now ship a generic picker per block. */}
      <Dialog open={mediaPickerOpen} onOpenChange={setMediaPickerOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid={`media-picker-dialog-${block.id}`}>
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-[#082c50]">
              Kies afbeelding uit Media Library
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Selecteer een bestaande afbeelding of upload een nieuwe.
              De gekozen afbeelding wordt direct gekoppeld aan dit
              contentblock.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <MediaLibrary
              pickMode
              onSelect={(item) => {
                if (!item) return;
                setLocal((prev) => ({
                  ...prev,
                  data: {
                    ...prev.data,
                    image_url: item.url || "",
                    image_alt:
                      prev.data?.image_alt ||
                      item.alt_text ||
                      item.title ||
                      "",
                    image_title:
                      prev.data?.image_title || item.title || "",
                  },
                }));
                setMediaPickerOpen(false);
                toast.success("Afbeelding gekoppeld — vergeet niet 'Block opslaan' te klikken.");
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </li>
  );
}

// ============================================================
// Sub-section: Locations
// ============================================================
function LocationsSubSection() {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const canCreate = hasPermission("website:create");
  const canEdit = hasPermission("website:edit");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/website/locations");
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const empty = {
    country: "", country_code: "", hub_label: "", address: "", phone: "", email: "",
    contact_person: "", opening_hours: "", button_link: "", active: true, sort_order: 0,
  };

  const onSave = async () => {
    try {
      if (editing.id) {
        await adminApi.put(`/admin/website/locations/${editing.id}`, editing);
      } else {
        await adminApi.post("/admin/website/locations", editing);
      }
      toast.success("Locatie opgeslagen");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    if (!isAdminOrOwner()) { toast.error("Alleen Owner/Admin kan locaties verwijderen."); return; }
    if (!window.confirm("Locatie verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/website/locations/${id}`);
      toast.success("Verwijderd");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="website-locations-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-gray-600">Beheer alle hub-locaties die op de publieke website worden getoond.</p>
        {canCreate && (
          <Button onClick={() => setEditing({ ...empty })} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="website-location-new">
            <Plus className="w-4 h-4 mr-1" /> Nieuwe locatie
          </Button>
        )}
      </div>

      {editing && (
        <div className="bg-white border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {["country", "country_code", "hub_label", "address", "phone", "email", "contact_person", "opening_hours", "button_link"].map((k) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide font-bold">
                  {k.replace(/_/g, " ")}
                  {k === "country_code" ? <span className="text-gray-400 text-[10px] normal-case ml-1">(ISO-2, bv NL, CW, US)</span> : null}
                </Label>
                <Input
                  value={editing[k] || ""}
                  onChange={(e) => setEditing({ ...editing, [k]: k === "country_code" ? e.target.value.toUpperCase().slice(0, 2) : e.target.value })}
                  className="rounded-none mt-1"
                  disabled={!canEdit}
                  maxLength={k === "country_code" ? 2 : undefined}
                  placeholder={k === "country_code" ? "NL" : undefined}
                  data-testid={`website-location-form-${k}`}
                />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Volgorde</Label>
              <Input type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} className="rounded-none mt-1" disabled={!canEdit} />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} disabled={!canEdit} />
              <Label className="text-xs uppercase tracking-wide font-bold">Actief</Label>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={onSave} className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" disabled={!canEdit} data-testid="website-location-save">
              <Save className="w-4 h-4 mr-1" /> Opslaan
            </Button>
            <Button onClick={() => setEditing(null)} variant="outline" className="rounded-none">Annuleren</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-6 text-gray-500">Laden…</div> :
         items.length === 0 ? <div className="p-6 text-gray-500">Geen locaties.</div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="py-3 px-4 font-bold">Land</th>
                <th className="py-3 px-4 font-bold">Hub</th>
                <th className="py-3 px-4 font-bold">Telefoon</th>
                <th className="py-3 px-4 font-bold">E-mail</th>
                <th className="py-3 px-4 font-bold">Actief</th>
                <th className="py-3 px-4 font-bold">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((l) => (
                <tr key={l.id} data-testid={`website-location-row-${l.id}`}>
                  <td className="py-3 px-4 font-bold whitespace-nowrap">
                    {l.country_code ? (
                      <span className="text-base mr-1.5" aria-hidden="true" data-testid={`website-location-flag-${l.id}`}>
                        {String.fromCodePoint(...l.country_code.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))}
                      </span>
                    ) : null}
                    {l.country}
                    {l.country_code ? <span className="ml-1 text-[10px] text-gray-400 font-normal">{l.country_code}</span> : null}
                  </td>
                  <td className="py-3 px-4">{l.hub_label}</td>
                  <td className="py-3 px-4 text-xs">{l.phone}</td>
                  <td className="py-3 px-4 text-xs">{l.email}</td>
                  <td className="py-3 px-4 text-xs">{l.active ? "Ja" : "Nee"}</td>
                  <td className="py-3 px-4 flex items-center gap-2">
                    {canEdit && <button onClick={() => setEditing({ ...l })} className="text-xs text-[#0055FF] hover:underline" data-testid={`website-location-edit-${l.id}`}>Bewerken</button>}
                    {isAdminOrOwner() && <button onClick={() => onDelete(l.id)} className="text-xs text-red-500 hover:underline" data-testid={`website-location-delete-${l.id}`}>Verwijderen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Sub-section: Tariffs
// ============================================================
function TariffsSubSection() {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const canCreate = hasPermission("website:create");
  const canEdit = hasPermission("website:edit");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/website/tariffs");
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const empty = {
    page_slug: "", title: "", description: "", price: 0, currency: "EUR",
    vat_included: true, line: "", note: "",
    price_type: "fixed", internal_note: "",
    active: true, sort_order: 0,
  };

  const onSave = async () => {
    if (!editing.title) {
      toast.error("Titel is verplicht."); return;
    }
    // Price required only for fixed/from_price
    if (editing.price_type !== "on_request" &&
        (editing.price === undefined || editing.price === null || editing.price === "")) {
      toast.error("Prijs is verplicht voor fixed of from_price tarieven."); return;
    }
    try {
      const payload = { ...editing };
      if (payload.price_type === "on_request") {
        payload.price = null;
      } else {
        payload.price = Number(payload.price);
      }
      // Use v2 endpoint for full price_type support
      if (editing.id) await adminApi.put(`/admin/website/tariffs-v2/${editing.id}`, payload);
      else await adminApi.post("/admin/website/tariffs-v2", payload);
      toast.success("Tarief opgeslagen");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    if (!isAdminOrOwner()) { toast.error("Alleen Owner/Admin kan tarieven verwijderen."); return; }
    if (!window.confirm("Tarief verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/website/tariffs/${id}`);
      toast.success("Verwijderd");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="website-tariffs-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-gray-600">Tarief- en prijsblokken die op de publieke website worden getoond. Webshop-producten komen in de volgende fase.</p>
        {canCreate && (
          <Button onClick={() => setEditing({ ...empty })} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="website-tariff-new">
            <Plus className="w-4 h-4 mr-1" /> Nieuw tarief
          </Button>
        )}
      </div>

      {editing && (
        <div className="bg-white border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Pagina-slug</Label>
              <Input value={editing.page_slug || ""} onChange={(e) => setEditing({ ...editing, page_slug: e.target.value })} className="rounded-none mt-1" placeholder="luchtvracht" data-testid="website-tariff-form-page-slug" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Titel</Label>
              <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-none mt-1" data-testid="website-tariff-form-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Prijs</Label>
              <Input type="number" step="0.01" value={editing.price ?? ""} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-none mt-1" disabled={editing.price_type === "on_request"} data-testid="website-tariff-form-price" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Prijs-type</Label>
              <Select value={editing.price_type || "fixed"} onValueChange={(v) => setEditing({ ...editing, price_type: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="website-tariff-form-price-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed (€ bedrag)</SelectItem>
                  <SelectItem value="from_price">Vanaf prijs</SelectItem>
                  <SelectItem value="on_request">Prijs op aanvraag</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Valuta</Label>
              <Input value={editing.currency || "EUR"} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Eenheid (per…)</Label>
              <Input value={editing.line || ""} onChange={(e) => setEditing({ ...editing, line: e.target.value })} className="rounded-none mt-1" placeholder="kg / m³ / pakket" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Volgorde</Label>
              <Input type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} className="rounded-none mt-1" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Label className="text-xs uppercase tracking-wide font-bold">Omschrijving</Label>
              <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Label className="text-xs uppercase tracking-wide font-bold">Disclaimer / notitie</Label>
              <Textarea rows={2} value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={!!editing.vat_included} onCheckedChange={(v) => setEditing({ ...editing, vat_included: v })} />
              <Label className="text-xs uppercase tracking-wide font-bold">Btw inclusief</Label>
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
              <Label className="text-xs uppercase tracking-wide font-bold">Actief</Label>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={onSave} className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" disabled={!canEdit} data-testid="website-tariff-save">
              <Save className="w-4 h-4 mr-1" /> Opslaan
            </Button>
            <Button onClick={() => setEditing(null)} variant="outline" className="rounded-none">Annuleren</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-6 text-gray-500">Laden…</div> :
         items.length === 0 ? <div className="p-6 text-gray-500">Geen tarieven.</div> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="py-3 px-4 font-bold">Pagina</th>
                <th className="py-3 px-4 font-bold">Titel</th>
                <th className="py-3 px-4 font-bold">Prijs</th>
                <th className="py-3 px-4 font-bold">Per</th>
                <th className="py-3 px-4 font-bold">BTW</th>
                <th className="py-3 px-4 font-bold">Actief</th>
                <th className="py-3 px-4 font-bold">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((t) => {
                const pt = t.price_type || "fixed";
                let priceCell;
                if (pt === "on_request") priceCell = <span className="text-[#0055FF] font-bold">Op aanvraag</span>;
                else if (pt === "from_price") priceCell = <span><span className="text-[10px] text-gray-500 mr-1 uppercase">vanaf</span>{t.currency} {Number(t.price).toFixed(2)}</span>;
                else priceCell = <>{t.currency} {Number(t.price).toFixed(2)}</>;
                return (
                <tr key={t.id} data-testid={`website-tariff-row-${t.id}`}>
                  <td className="py-3 px-4 font-mono text-xs">{t.page_slug || "—"}</td>
                  <td className="py-3 px-4 font-bold">
                    {t.title}
                    {t.has_options && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-indigo-100 text-indigo-700 px-1.5 py-0.5">
                        <Layers className="w-3 h-3" /> {(t.options || []).length} opties
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">{priceCell}</td>
                  <td className="py-3 px-4 text-xs">{t.line || "—"}</td>
                  <td className="py-3 px-4 text-xs">{t.vat_included ? "incl." : "excl."}</td>
                  <td className="py-3 px-4 text-xs">{t.active ? "Ja" : "Nee"}</td>
                  <td className="py-3 px-4 flex items-center gap-2">
                    {canEdit && <button onClick={() => setEditing({ ...t })} className="text-xs text-[#0055FF] hover:underline" data-testid={`website-tariff-edit-${t.id}`}>Bewerken</button>}
                    {canEdit && <button onClick={() => setManaging(t)} className="text-xs text-indigo-600 hover:underline" data-testid={`website-tariff-options-${t.id}`}>Opties</button>}
                    {isAdminOrOwner() && <button onClick={() => onDelete(t.id)} className="text-xs text-red-500 hover:underline" data-testid={`website-tariff-delete-${t.id}`}>Verwijderen</button>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <TariffOptionsManager
        tariff={managing}
        onClose={() => { setManaging(null); load(); }}
        canEdit={canEdit}
        canDelete={isAdminOrOwner()}
      />
    </div>
  );
}

// ---------- Options Manager (Phase 9) ----------

function TariffOptionsManager({ tariff, onClose, canEdit, canDelete }) {
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState({
    option_name: "", description: "", min_value: "", max_value: "",
    unit: "m³", price: "", currency: "EUR", price_type: "fixed",
    sort_order: 0, active: true,
  });

  useEffect(() => {
    if (tariff) {
      setOptions((tariff.options || []).map((o) => ({ ...o })));
    } else {
      setOptions([]);
    }
  }, [tariff]);

  if (!tariff) return null;

  const saveAll = async () => {
    setBusy(true);
    try {
      const cleaned = options
        .filter((o) => o.option_name && o.option_name.trim())
        .map((o) => ({
          ...o,
          min_value: o.min_value === "" || o.min_value === null ? null : Number(o.min_value),
          max_value: o.max_value === "" || o.max_value === null ? null : Number(o.max_value),
          price: o.price === "" || o.price === null ? null : Number(o.price),
          sort_order: Number(o.sort_order || 0),
        }));
      await adminApi.put(`/admin/website/tariffs/${tariff.id}/options`, { options: cleaned });
      toast.success(`${cleaned.length} opties opgeslagen`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addOption = () => {
    if (!adding.option_name || !adding.option_name.trim()) {
      toast.error("Naam is verplicht");
      return;
    }
    setOptions((arr) => [...arr, { ...adding, id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2,6)}` }]);
    setAdding({
      option_name: "", description: "", min_value: "", max_value: "",
      unit: adding.unit, currency: adding.currency, price: "", price_type: "fixed",
      sort_order: options.length + 1, active: true,
    });
  };

  const updateOpt = (idx, patch) => {
    setOptions((arr) => arr.map((o, i) => i === idx ? { ...o, ...patch } : o));
  };

  const removeOpt = (idx) => {
    setOptions((arr) => arr.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    setOptions((arr) => {
      const out = arr.slice();
      const j = idx + dir;
      if (j < 0 || j >= out.length) return out;
      [out[idx], out[j]] = [out[j], out[idx]];
      return out.map((o, i) => ({ ...o, sort_order: i + 1 }));
    });
  };

  return (
    <Dialog open={!!tariff} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="tariff-options-dialog">
        <DialogHeader>
          <DialogTitle>Opties beheren — {tariff.title}</DialogTitle>
          <DialogDescription>
            Voeg prijsstaffels toe (bijv. per m³ of per kg). Alleen actieve opties zijn publiek zichtbaar.
            Hoofdprijs blijft fallback voor pagina's die geen opties tonen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {options.length === 0 && (
            <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2">
              Nog geen opties. Voeg er hieronder één toe.
            </div>
          )}
          {options.map((o, idx) => (
            <div key={o.id || idx} className="bg-white border border-gray-200 p-3" data-testid={`tariff-option-row-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <Label className="text-[10px] uppercase">Naam *</Label>
                  <Input className="rounded-none" value={o.option_name || ""} onChange={(e) => updateOpt(idx, { option_name: e.target.value })} data-testid={`tariff-option-name-${idx}`} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">Min</Label>
                  <Input className="rounded-none" type="number" step="0.01" value={o.min_value ?? ""} onChange={(e) => updateOpt(idx, { min_value: e.target.value })} data-testid={`tariff-option-min-${idx}`} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">Max</Label>
                  <Input className="rounded-none" type="number" step="0.01" value={o.max_value ?? ""} placeholder="∞" onChange={(e) => updateOpt(idx, { max_value: e.target.value })} data-testid={`tariff-option-max-${idx}`} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">Eenheid</Label>
                  <Input className="rounded-none" value={o.unit || ""} onChange={(e) => updateOpt(idx, { unit: e.target.value })} placeholder="m³ / kg / zending" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase">Prijs (EUR)</Label>
                  <Input className="rounded-none" type="number" step="0.01" value={o.price ?? ""} onChange={(e) => updateOpt(idx, { price: e.target.value })} disabled={o.price_type === "on_request"} data-testid={`tariff-option-price-${idx}`} />
                </div>
                <div className="md:col-span-4">
                  <Label className="text-[10px] uppercase">Beschrijving</Label>
                  <Input className="rounded-none" value={o.description || ""} onChange={(e) => updateOpt(idx, { description: e.target.value })} />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase">Type prijs</Label>
                  <select value={o.price_type || "fixed"} onChange={(e) => updateOpt(idx, { price_type: e.target.value })} className="w-full rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10">
                    <option value="fixed">Vast</option>
                    <option value="from_price">Vanaf</option>
                    <option value="on_request">Op aanvraag</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex items-end gap-2">
                  <Switch checked={o.active !== false} onCheckedChange={(v) => updateOpt(idx, { active: !!v })} />
                  <Label className="text-[10px] uppercase">Actief</Label>
                </div>
                <div className="md:col-span-3 flex items-end justify-end gap-1">
                  <Button type="button" size="icon" variant="outline" className="rounded-none" onClick={() => move(idx, -1)} disabled={idx === 0}><ChevronUp className="w-4 h-4" /></Button>
                  <Button type="button" size="icon" variant="outline" className="rounded-none" onClick={() => move(idx, 1)} disabled={idx === options.length - 1}><ChevronDown className="w-4 h-4" /></Button>
                  {canDelete && (
                    <Button type="button" size="icon" variant="outline" className="rounded-none text-red-600" onClick={() => removeOpt(idx)} data-testid={`tariff-option-delete-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="bg-blue-50 border border-blue-200 p-3" data-testid="tariff-option-add-row">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#0055FF] mb-2">Nieuwe optie toevoegen</div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-4"><Input className="rounded-none" placeholder="Naam (bv. 'Tot 0.5 m³')" value={adding.option_name} onChange={(e) => setAdding({ ...adding, option_name: e.target.value })} data-testid="tariff-option-new-name" /></div>
              <div className="md:col-span-2"><Input className="rounded-none" type="number" step="0.01" placeholder="Min" value={adding.min_value} onChange={(e) => setAdding({ ...adding, min_value: e.target.value })} /></div>
              <div className="md:col-span-2"><Input className="rounded-none" type="number" step="0.01" placeholder="Max" value={adding.max_value} onChange={(e) => setAdding({ ...adding, max_value: e.target.value })} /></div>
              <div className="md:col-span-2"><Input className="rounded-none" placeholder="Eenheid" value={adding.unit} onChange={(e) => setAdding({ ...adding, unit: e.target.value })} /></div>
              <div className="md:col-span-2"><Input className="rounded-none" type="number" step="0.01" placeholder="Prijs" value={adding.price} onChange={(e) => setAdding({ ...adding, price: e.target.value })} data-testid="tariff-option-new-price" /></div>
              <div className="md:col-span-12 flex justify-end">
                <Button type="button" onClick={addOption} disabled={!canEdit} className="rounded-none bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="tariff-option-add">
                  <Plus className="w-4 h-4 mr-1" /> Optie toevoegen
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Annuleren</Button>
          <Button onClick={saveAll} disabled={!canEdit || busy} className="rounded-none bg-[#0A192F] hover:bg-[#050505] text-white" data-testid="tariff-options-save">
            <Save className="w-4 h-4 mr-1" /> Opties opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// ============================================================
// Main section export
// ============================================================

/**
 * Phase 20D — Websitebeheer navigation config.
 *
 * Single source of truth voor alle CMS-modules. Items behouden hun bestaande
 * `value` (= tab-key) en `data-testid="website-tab-{value}"` zodat alle
 * regressie- en Playwright-tests blijven werken zonder aanpassing. Volgorde
 * binnen een groep is de gewenste UI-volgorde.
 *
 * Toekomstige CMS-secties: voeg een nieuw item toe aan de juiste groep —
 * geen markup-wijziging nodig.
 */
const WEBSITE_NAV_GROUPS = [
  {
    key: "content",
    label: "Content",
    description: "Publieke pagina's, homepage en FAQ-content",
    items: [
      { value: "pages", label: "Pagina's", icon: FileText, hint: "Beheer publieke pagina's, SEO, blokken en versiehistorie" },
      { value: "homepage", label: "Homepage", icon: Home, hint: "Hero, service cards, sectie-koppen en CTA-band" },
      { value: "faq-categories", label: "FAQ-categorieën", icon: HelpCircle, hint: "11 categorieën, 77 vragen — Google FAQPage schema" },
    ],
  },
  {
    key: "page-groups",
    label: "Pagina-groepen",
    description: "Banner, USP-badges, intro en CTA-band per pagina-type",
    items: [
      { value: "service-pages", label: "Servicepagina's", icon: Ship, hint: "Zeevracht, Luchtvracht, Verhuizing, Zakelijk" },
      { value: "tool-pages", label: "Tool-pagina's", icon: Wrench, hint: "CBM Calculator, Volumemeter, Paklijst, Boekingsformulier" },
      { value: "info-pages", label: "Informatiepagina's", icon: Info, hint: "Documenten, BTW-teruggave, Contact" },
      { value: "misc-pages", label: "Overige pagina's", icon: Boxes, hint: "Webshop, Offerte share, MY5999" },
      { value: "extra-pages", label: "Extra pagina's", icon: Sparkles, hint: "Hubs, Over ons, FAQ, Pickup, Shipping to Curaçao" },
      { value: "destinations", label: "Bestemmingen", icon: MapPin, hint: "Aruba, Bonaire, Sint Maarten, Suriname, Curaçao" },
      { value: "online-payment", label: "Online betalen", icon: CreditCard, hint: "PageBanner /online-betalen (Stripe blijft onveranderd)" },
    ],
  },
  {
    key: "data-settings",
    label: "Data & instellingen",
    description: "Locaties, tarieven, media en homepage-routes",
    items: [
      { value: "locations", label: "Locaties", icon: MapPin, hint: "Hubs, adressen, contactgegevens en openingstijden" },
      { value: "tariffs", label: "Tarieven", icon: Tag, hint: "Publieke tarieven en calculatiegegevens" },
      { value: "media", label: "Media", icon: ImageIcon, hint: "Upload en beheer afbeeldingen/documenten" },
      { value: "routes", label: "Routes & Bestemmingen", icon: Route, hint: "Routekaarten zichtbaar op de homepage" },
      { value: "trusted", label: "Trusted Brands", icon: Award, hint: "Bewegende logo-strip op de homepage" },
    ],
  },
];

const _allNavItems = WEBSITE_NAV_GROUPS.flatMap(
  (g) => g.items.map((it) => ({ ...it, group: g.key, groupLabel: g.label })),
);

function _findGroupForTab(tab) {
  for (const g of WEBSITE_NAV_GROUPS) {
    if (g.items.some((it) => it.value === tab)) return g.key;
  }
  return WEBSITE_NAV_GROUPS[0].key;
}

/**
 * Top-level grouped navigatie voor Websitebeheer.
 *
 * Layout:
 *   [Content] [Pagina-groepen] [Data & instellingen]              [Zoek …]
 *   [Item 1] [Item 2] [Item 3] ...   (binnen actieve groep)
 *
 * Mobile (<md): groepenrij + native <select> met optgroups als fallback.
 * Search: filtert real-time over ALLE items van alle groepen.
 */
function WebsiteSubNav({ tab, setTab }) {
  const [activeGroup, setActiveGroup] = useState(() => _findGroupForTab(tab));
  const [query, setQuery] = useState("");

  // Wanneer `tab` extern wijzigt (bv. PagesSubSection roept setTab("homepage")
  // op na Save), houd de actieve groep in sync.
  useEffect(() => { setActiveGroup(_findGroupForTab(tab)); }, [tab]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return _allNavItems.filter(
      (it) => it.label.toLowerCase().includes(q)
           || it.hint.toLowerCase().includes(q)
           || it.value.toLowerCase().includes(q)
           || it.groupLabel.toLowerCase().includes(q),
    );
  }, [query]);

  const activeGroupCfg = WEBSITE_NAV_GROUPS.find((g) => g.key === activeGroup) || WEBSITE_NAV_GROUPS[0];

  return (
    <div className="space-y-2" data-testid="website-subnav">
      {/* TOP ROW — groepen + zoek */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 bg-white border border-gray-200 p-2">
        <div className="flex flex-wrap gap-1.5" data-testid="website-nav-groups">
          {WEBSITE_NAV_GROUPS.map((g) => {
            const isActive = activeGroup === g.key;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => { setActiveGroup(g.key); setQuery(""); }}
                className={`px-3 py-2 text-xs font-bold uppercase tracking-wide rounded-none transition-colors min-h-[44px] md:min-h-0 md:py-1.5 ${
                  isActive
                    ? "bg-[#0A192F] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                data-testid={`website-nav-group-${g.key}`}
                title={g.description}
              >
                {g.label}
                <span className="ml-1.5 text-[10px] opacity-70">{g.items.length}</span>
              </button>
            );
          })}
        </div>
        <div className="md:flex-1" />
        <div className="relative md:max-w-[280px] w-full">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="search"
            placeholder="Zoek beheeronderdeel…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-none h-9 text-sm pl-7"
            data-testid="website-nav-search"
          />
        </div>
      </div>

      {/* MID/BOTTOM ROW — items van actieve groep OF zoekresultaten */}
      {filtered ? (
        <div className="flex flex-wrap gap-1.5 bg-white border border-gray-200 p-2" data-testid="website-nav-search-results">
          {filtered.length === 0 ? (
            <p className="text-xs text-gray-500 px-2 py-1.5" data-testid="website-nav-search-empty">
              Geen onderdeel gevonden voor "{query}".
            </p>
          ) : (
            filtered.slice(0, 16).map((it) => {
              const Icon = it.icon;
              const isActive = tab === it.value;
              return (
                <button
                  key={it.value}
                  type="button"
                  onClick={() => { setTab(it.value); setActiveGroup(it.group); setQuery(""); }}
                  title={it.hint}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-none border transition-colors ${
                    isActive
                      ? "border-[#0A192F] bg-[#0A192F] text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-[#0A192F] hover:text-[#0A192F]"
                  }`}
                  data-testid={`website-tab-${it.value}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {it.label}
                  <span className="text-[10px] opacity-60 ml-1">— {it.groupLabel}</span>
                </button>
              );
            })
          )}
        </div>
      ) : (
        <>
          {/* Desktop / tablet ≥ md */}
          <div
            className="hidden md:flex flex-wrap gap-1.5 bg-white border border-gray-200 p-2"
            data-testid={`website-nav-items-${activeGroup}`}
          >
            {activeGroupCfg.items.map((it) => {
              const Icon = it.icon;
              const isActive = tab === it.value;
              return (
                <button
                  key={it.value}
                  type="button"
                  onClick={() => setTab(it.value)}
                  title={it.hint}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-none border transition-colors ${
                    isActive
                      ? "border-[#0A192F] bg-[#0A192F] text-white font-bold"
                      : "border-gray-200 bg-white text-gray-700 hover:border-[#0A192F] hover:text-[#0A192F]"
                  }`}
                  data-testid={`website-tab-${it.value}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {it.label}
                </button>
              );
            })}
          </div>
          {/* Mobile fallback */}
          <div className="md:hidden bg-white border border-gray-200 p-2">
            <Label className="text-[10px] uppercase font-bold text-gray-500 mb-1 block">
              {activeGroupCfg.label}
            </Label>
            <select
              value={tab}
              onChange={(e) => setTab(e.target.value)}
              className="w-full rounded-none border border-gray-300 px-2 py-2 text-sm bg-white"
              data-testid="website-tab-mobile-select"
            >
              {WEBSITE_NAV_GROUPS.map((g) => (
                <optgroup key={g.key} label={g.label}>
                  {g.items.map((it) => (
                    <option key={it.value} value={it.value}>{it.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1.5">{activeGroupCfg.description}</p>
          </div>
        </>
      )}
    </div>
  );
}

export default function WebsiteSection() {
  const [tab, setTab] = useState("pages");

  return (
    <div data-testid="admin-section-website">
      <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight">Websitebeheer</h1>
      <p className="text-sm text-gray-600 mt-1">Beheer pagina's, content blocks, locaties en tarieven van de publieke website.</p>

      <div className="mt-6">
        <WebsiteSubNav tab={tab} setTab={setTab} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-2">
        {/* Hidden TabsList — required by Radix for keyboard nav. Buttons hierboven sturen `tab` aan. */}
        <TabsList className="sr-only" aria-hidden="true">
          {WEBSITE_NAV_GROUPS.flatMap((g) => g.items).map((it) => (
            <TabsTrigger key={it.value} value={it.value}>{it.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="pages" className="mt-4"><PagesSubSection onOpenHomepageTab={(targetTab) => setTab(targetTab || "homepage")} /></TabsContent>
        <TabsContent value="locations" className="mt-4"><LocationsSubSection /></TabsContent>
        <TabsContent value="tariffs" className="mt-4"><TariffsSubSection /></TabsContent>
        <TabsContent value="media" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Media Library</h2>
              <p className="text-sm text-gray-600 mt-1">Upload en beheer afbeeldingen die op de publieke website worden gebruikt.</p>
            </div>
            <MediaLibrary />
          </div>
        </TabsContent>
        <TabsContent value="routes" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Routes &amp; Bestemmingen</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer de routekaarten die zichtbaar zijn op de homepage. Zonder code-deploy aanpasbaar.</p>
            </div>
            <RoutesSubSection />
          </div>
        </TabsContent>
        <TabsContent value="trusted" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Trusted Brands marquee</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer de bewegende logo-strip op de homepage. Upload logo's in Media Library, koppel ze hier per merk.</p>
            </div>
            <TrustedBrandsSubSection />
          </div>
        </TabsContent>
        <TabsContent value="homepage" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Homepage inhoud</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer de hero, service cards, sectie-koppen en CTA-band van de publieke homepage. Zonder code-deploy aanpasbaar.</p>
            </div>
            <HomepageSubSection />
          </div>
        </TabsContent>
        <TabsContent value="online-payment" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Online betalen banner</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer de premium PageBanner bovenaan <strong>/online-betalen</strong>. Stripe Buy Button + "Belangrijk voor vertrek" blijven ongewijzigd.</p>
            </div>
            <OnlinePaymentSubSection />
          </div>
        </TabsContent>
        <TabsContent value="service-pages" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Servicepagina's</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer hero, intro, voordelen-cards en CTA-band van de service-pagina's: <strong>Zeevracht</strong>, <strong>Luchtvracht</strong>, <strong>Shop, Drop &amp; Ship</strong>, <strong>Verhuizing</strong> en <strong>Zakelijk</strong>.</p>
            </div>
            <ServicePagesSubSection />
          </div>
        </TabsContent>
        <TabsContent value="tool-pages" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Tool-pagina's</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer banner, USP-badges, intro en CTA-band van de tool-pagina's: <strong>CBM Calculator</strong>, <strong>Volumemeter</strong>, <strong>Factuur maken</strong>, <strong>Paklijst</strong>, <strong>Boekingsformulier</strong> en <strong>Word agent</strong>. De tools zelf (berekeningen, PDF-flows, e-mails) blijven ongewijzigd.</p>
            </div>
            <ToolPagesSubSection />
          </div>
        </TabsContent>
        <TabsContent value="info-pages" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Informatiepagina's</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer banner, USP-badges, intro en CTA-band van de informatie-pagina's: <strong>Documenten</strong>, <strong>BTW-teruggave</strong> en <strong>Contact</strong>. De documentenlijst &amp; downloads, het contactformulier, het offerteformulier en de bedrijfsgegevens blijven ongewijzigd en blijven via hun eigen tabs beheerbaar.</p>
            </div>
            <InfoPagesSubSection />
          </div>
        </TabsContent>
        <TabsContent value="misc-pages" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Overige pagina's</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer banner, USP-badges, intro en CTA-band van de resterende publieke pagina's: <strong>Webshop</strong>, <strong>Offerte share page</strong> (token-gated) en <strong>MY5999</strong>. Webshop checkout / Stripe, offerte PDF-downloads + token-security en MY5999 login-flow blijven volledig ongewijzigd.</p>
            </div>
            <MiscPagesSubSection />
          </div>
        </TabsContent>
        <TabsContent value="extra-pages" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Extra pagina's</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer banner, USP-badges, intro en CTA-band van de informatieve restpagina's: <strong>Hubs</strong>, <strong>Over ons</strong>, <strong>FAQ</strong>, <strong>Pickup &amp; Delivery</strong>, <strong>Pickup Calculator</strong> en <strong>Shipping to Curaçao</strong>. Hubs-lijst (met vlaggen via <em>country_code</em>) en pickup-calculator-logica blijven via hun eigen modules werken.</p>
            </div>
            <ExtraPagesSubSection />
          </div>
        </TabsContent>
        <TabsContent value="destinations" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">Bestemmingen (SEO Sprint 2)</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer de inhoud van de SEO-destination landingpages: <strong>Aruba</strong>, <strong>Bonaire</strong>, <strong>Sint Maarten</strong> en <strong>Suriname</strong>. Banner, intro, service-blokken, FAQ, CTA-band en SEO meta-data per bestemming. URLs: /shipping-to-{`{slug}`}.</p>
            </div>
            <DestinationsSubSection />
          </div>
        </TabsContent>
        <TabsContent value="faq-categories" className="mt-4">
          <div className="bg-white border border-gray-200 p-6 rounded-2xl">
            <div className="mb-5">
              <h2 className="font-chivo font-black text-xl text-[#082c50]">FAQ-categorieën (SEO Sprint 4B)</h2>
              <p className="text-sm text-gray-600 mt-1">Beheer de 11 categorieën van de uitgebreide /faq pagina: van Verzenden & Bestemmingen tot Lokale vestigingen. Alleen vragen die hier staan worden in het FAQPage JSON-LD schema gerenderd (Google policy).</p>
            </div>
            <FAQCategoriesSubSection />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
